/**
 * MobileDrawer Component
 * Hamburger menu drawer for mobile navigation
 * Converted from MUI to Tailwind CSS
 */

import { Fragment, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import {
  HomeIcon,
  ArrowsRightLeftIcon,
  BuildingLibraryIcon,
  BuildingOffice2Icon,
  ChartBarIcon,
  ClipboardDocumentListIcon,
  DocumentChartBarIcon,
  DocumentDuplicateIcon,
  ClipboardDocumentCheckIcon,
  DocumentCheckIcon,
  InboxArrowDownIcon,
  MagnifyingGlassIcon,
  UserIcon,
  Cog6ToothIcon,
  ArrowLeftStartOnRectangleIcon,
  XMarkIcon,
  ShieldCheckIcon,
  ChevronDownIcon,
  DocumentTextIcon,
  ArchiveBoxIcon,
  BookOpenIcon,
} from '@heroicons/react/24/outline';
import { useOathAuth } from '../../services/oathAuthContext';
import { useOathNotifications } from '../../services/oathNotificationContext';
import { usePermissions } from '../../hooks/usePermissions';
import { OathAnimatedPanel } from './OathTableComponents';

/**
 * MobileDrawer - Side navigation drawer
 * @param {Object} props
 * @param {boolean} props.open - Whether drawer is open
 * @param {() => void} props.onClose - Close handler
 */
const MobileDrawer = ({ open, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useOathAuth();
  const { notifications } = useOathNotifications();
  const notificationCount = notifications.filter((n) => n.contextTypeCode !== 'LB').length;
  const {
    canAccessTransferBonds,
    canAccessLargeBonds,
    canAccessPowerReporting,
    canAccessPowerLog,
    canAccessPowerInquiry,
    canAccessPowerInventory,
    canAccessForms,
    canAccessDischarges,
    canAccessReports,
    canAccessAdmin,
    canAccessSubAgencyAR,
    canAccessAR,
  } = usePermissions();

  // Track which sub-menus are expanded
  const [expandedMenus, setExpandedMenus] = useState([]); // All collapsed by default

  const toggleSubMenu = (label) => {
    setExpandedMenus((prev) =>
      prev.includes(label) ? [] : [label]
    );
  };

  const handleLogout = () => {
    onClose();
    logout();
    navigate('/');
  };

  const isActive = (path) => {
    // Exact match for dashboard
    if (path === '/app/dashboard') {
      return location.pathname === path;
    }
    // For other paths, check if current path starts with the nav item path
    return location.pathname.startsWith(path);
  };

  // Check if any child of a parent menu is active
  const isParentActive = (children) => {
    return children?.some((child) => child.show && isActive(child.path));
  };

  const navItems = [
    {
      label: 'Dashboard',
      tooltip: 'Agency snapshot with liability, trends, and key metrics',
      icon: HomeIcon,
      path: '/app/dashboard',
      show: true,
    },
    {
      label: 'Powers',
      tooltip: 'Search, report, and manage bail powers',
      icon: DocumentTextIcon,
      show: canAccessPowerInquiry || canAccessPowerReporting || canAccessPowerLog || canAccessDischarges || canAccessPowerInventory,
      children: [
        {
          label: 'Power Inquiry',
          tooltip: 'Look up powers by defendant, number, or date across agencies',
          icon: MagnifyingGlassIcon,
          path: '/app/power-inquiry',
          show: canAccessPowerInquiry,
        },
        {
          label: 'Power Reporting',
          tooltip: 'Create execution reports, manage batches, and submit payments',
          icon: ChartBarIcon,
          path: '/app/power-reports',
          show: canAccessPowerReporting,
        },
        {
          label: 'Power Discharges',
          tooltip: 'Request bond discharges with proof documents',
          icon: DocumentCheckIcon,
          path: '/app/discharges',
          show: canAccessDischarges,
        },
        {
          label: 'Power Log',
          tooltip: 'Record and track daily power execution entries',
          icon: ClipboardDocumentListIcon,
          path: '/app/power-log',
          show: canAccessPowerLog,
        },
        {
          label: 'Power Receipts',
          tooltip: 'Confirm receipt of powers and verify delivery',
          icon: InboxArrowDownIcon,
          path: '/app/power-receipts',
          show: canAccessPowerReporting,
        },
        {
          label: 'Power Inventory',
          tooltip: 'View unexecuted power counts and assignments by agency',
          icon: ArchiveBoxIcon,
          path: '/app/power-inventory',
          show: canAccessPowerInventory,
        },
      ],
    },
    {
      label: 'Specialty Bonds',
      tooltip: 'Transfer bonds and large bond applications',
      icon: BuildingLibraryIcon,
      show: canAccessTransferBonds || canAccessLargeBonds,
      children: [
        {
          label: 'Transfer Bonds',
          tooltip: 'Manage bail bond transfers between agents',
          icon: ArrowsRightLeftIcon,
          path: '/app/transfer-bonds',
          show: canAccessTransferBonds,
          badge: notificationCount,
        },
        {
          label: 'Large Bonds',
          tooltip: 'Apply for and manage high-value bond authorizations',
          icon: BuildingLibraryIcon,
          path: '/app/large-bonds',
          show: canAccessLargeBonds,
        },
      ],
    },
    {
      label: 'Forms',
      tooltip: 'Browse and download forms',
      icon: DocumentDuplicateIcon,
      path: '/app/forms',
      show: canAccessForms,
    },
    {
      label: 'Report Center',
      tooltip: 'Generate statements, liability, and forfeiture reports',
      icon: DocumentChartBarIcon,
      path: '/app/reports',
      show: canAccessReports,
    },
    {
      label: 'Sub-Agency A/R',
      tooltip: 'A/R aging breakdown across all sub-agencies',
      icon: BuildingOffice2Icon,
      path: '/app/sub-agency-ar',
      show: canAccessSubAgencyAR && canAccessAR,
    },
    {
      label: 'Agency Manager',
      tooltip: 'Agency profiles, users, and payment accounts',
      icon: ShieldCheckIcon,
      path: '/app/admin',
      show: canAccessAdmin,
    },
  ];

  const secondaryItems = [
    {
      label: 'Profile',
      tooltip: 'View and edit your profile and agency assignments',
      icon: UserIcon,
      path: '/app/profile',
      show: true,
    },
    {
      label: 'Settings',
      tooltip: 'Configure preferences and notification settings',
      icon: Cog6ToothIcon,
      path: '/app/settings',
      show: true,
    },
    {
      label: 'Resources',
      tooltip: 'Training videos, guides, and tutorials',
      icon: BookOpenIcon,
      path: '/app/resources',
      show: true,
    },
  ];

  const userInitials = user
    ? `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`.toUpperCase()
    : '?';

  return (
    <Transition show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        {/* Backdrop */}
        <TransitionChild
          as={Fragment}
          enter="ease-in-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in-out duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </TransitionChild>

        {/* Drawer panel */}
        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 left-0 flex max-w-full">
              <TransitionChild
                as={Fragment}
                enter="transform transition ease-in-out duration-300"
                enterFrom="-translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-300"
                leaveFrom="translate-x-0"
                leaveTo="-translate-x-full"
              >
                <DialogPanel className="pointer-events-auto w-[min(280px,85vw)] bg-white dark:bg-gray-900 shadow-xl flex flex-col h-full overflow-hidden">
                  {/* Header with gradient and accent */}
                  <div className="bg-gradient-to-br from-primary to-primary-dark text-white">
                    {/* Orange accent bar */}
                    <div className="h-1 bg-gradient-to-r from-orange-400 via-orange-500 to-orange-400" />
                    <div className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-full bg-white dark:bg-gray-100 text-primary flex items-center justify-center font-bold text-sm shadow-lg">
                          {userInitials}
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-sm leading-tight truncate">
                            {user?.firstName} {user?.lastName}
                          </p>
                          <p className="text-xs text-white/80 truncate">
                            @{user?.userName}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={onClose}
                        className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
                        aria-label="Close menu"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>

                  {/* Primary Navigation */}
                  <nav className="flex-1 overflow-y-auto py-3 dark:bg-gray-900">
                    <div className="px-3 mb-2">
                      <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Main Menu</p>
                    </div>
                    <ul className="space-y-1 px-2">
                      {navItems
                        .filter((item) => item.show)
                        .map((item) => {
                          const Icon = item.icon;
                          const hasChildren = item.children && item.children.length > 0;
                          const isExpanded = expandedMenus.includes(item.label);
                          const active = hasChildren ? isParentActive(item.children) : isActive(item.path);

                          // Item with children (sub-menu)
                          if (hasChildren) {
                            const visibleChildren = item.children.filter((child) => child.show);
                            if (visibleChildren.length === 0) return null;
                            const parentBadge = visibleChildren.reduce((sum, c) => sum + (c.badge || 0), 0);

                            return (
                              <li key={item.label}>
                                {/* Parent item - toggles sub-menu */}
                                <button
                                  onClick={() => toggleSubMenu(item.label)}
                                  title={item.tooltip}
                                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all ${
                                    active
                                      ? 'bg-primary/10 dark:bg-primary/20 text-primary dark:text-primary-light'
                                      : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800'
                                  }`}
                                >
                                  <Icon
                                    className={`h-5 w-5 flex-shrink-0 ${
                                      active ? 'text-primary dark:text-primary-light' : 'text-gray-400'
                                    }`}
                                  />
                                  <span className={active ? 'font-semibold' : 'font-medium'}>{item.label}</span>
                                  {!isExpanded && parentBadge > 0 && (
                                    <span className="ml-auto min-w-[20px] h-5 px-1.5 flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full">
                                      {parentBadge > 99 ? '99+' : parentBadge}
                                    </span>
                                  )}
                                  <ChevronDownIcon
                                    className={`${!isExpanded && parentBadge > 0 ? '' : 'ml-auto'} h-4 w-4 text-gray-400 transition-transform duration-200 ${
                                      isExpanded ? 'rotate-180' : ''
                                    }`}
                                  />
                                </button>

                                {/* Sub-menu items */}
                                <OathAnimatedPanel open={isExpanded}>
                                  <ul className="mt-1 ml-4 pl-4 border-l-2 border-gray-200 dark:border-gray-700 space-y-1">
                                    {visibleChildren.map((child) => {
                                      const ChildIcon = child.icon;
                                      const childActive = isActive(child.path);
                                      return (
                                        <li key={child.path}>
                                          <Link
                                            to={child.path}
                                            onClick={onClose}
                                            title={child.tooltip}
                                            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-all ${
                                              childActive
                                                ? 'bg-primary text-white shadow-md shadow-primary/20'
                                                : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                                            }`}
                                          >
                                            <ChildIcon
                                              className={`h-4 w-4 flex-shrink-0 ${
                                                childActive ? 'text-white' : 'text-gray-400'
                                              }`}
                                            />
                                            <span className={`text-sm ${childActive ? 'font-semibold' : 'font-medium'}`}>
                                              {child.label}
                                            </span>
                                            {child.badge > 0 && (
                                              <span className="ml-auto min-w-[20px] h-5 px-1.5 flex items-center justify-center bg-red-500 text-white text-[10px] font-bold rounded-full">
                                                {child.badge > 99 ? '99+' : child.badge}
                                              </span>
                                            )}
                                          </Link>
                                        </li>
                                      );
                                    })}
                                  </ul>
                                </OathAnimatedPanel>
                              </li>
                            );
                          }

                          // Regular item (no children)
                          return (
                            <li key={item.path}>
                              <Link
                                to={item.path}
                                onClick={onClose}
                                title={item.tooltip}
                                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all ${
                                  active
                                    ? 'bg-primary text-white shadow-md shadow-primary/20'
                                    : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800'
                                }`}
                              >
                                <Icon
                                  className={`h-5 w-5 flex-shrink-0 ${
                                    active ? 'text-white' : 'text-gray-400'
                                  }`}
                                />
                                <span className={active ? 'font-semibold' : 'font-medium'}>{item.label}</span>
                              </Link>
                            </li>
                          );
                        })}
                    </ul>

                    {/* Divider */}
                    <div className="my-4 mx-3 border-t border-gray-200 dark:border-gray-700" />

                    {/* Secondary Navigation */}
                    <div className="px-3 mb-2">
                      <p className="text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Account</p>
                    </div>
                    <ul className="space-y-1 px-2">
                      {secondaryItems
                        .filter((item) => item.show)
                        .map((item) => {
                          const Icon = item.icon;
                          const hasChildren = item.children && item.children.length > 0;
                          const isExpanded = expandedMenus.includes(item.label);
                          const active = hasChildren ? isParentActive(item.children) : isActive(item.path);

                          if (hasChildren) {
                            const visibleChildren = item.children.filter((child) => child.show);
                            if (visibleChildren.length === 0) return null;

                            return (
                              <li key={item.label}>
                                <button
                                  onClick={() => toggleSubMenu(item.label)}
                                  title={item.tooltip}
                                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all ${
                                    active
                                      ? 'bg-primary/10 dark:bg-primary/20 text-primary dark:text-primary-light'
                                      : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800'
                                  }`}
                                >
                                  <Icon
                                    className={`h-5 w-5 flex-shrink-0 ${
                                      active ? 'text-primary dark:text-primary-light' : 'text-gray-400'
                                    }`}
                                  />
                                  <span className={active ? 'font-semibold' : 'font-medium'}>{item.label}</span>
                                  <ChevronDownIcon
                                    className={`ml-auto h-4 w-4 text-gray-400 transition-transform duration-200 ${
                                      isExpanded ? 'rotate-180' : ''
                                    }`}
                                  />
                                </button>

                                <OathAnimatedPanel open={isExpanded}>
                                  <ul className="mt-1 ml-4 pl-4 border-l-2 border-gray-200 dark:border-gray-700 space-y-1">
                                    {visibleChildren.map((child) => {
                                      const ChildIcon = child.icon;
                                      const childActive = isActive(child.path);
                                      return (
                                        <li key={child.path}>
                                          <Link
                                            to={child.path}
                                            onClick={onClose}
                                            title={child.tooltip}
                                            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-all ${
                                              childActive
                                                ? 'bg-primary text-white shadow-md shadow-primary/20'
                                                : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                                            }`}
                                          >
                                            <ChildIcon
                                              className={`h-4 w-4 flex-shrink-0 ${
                                                childActive ? 'text-white' : 'text-gray-400'
                                              }`}
                                            />
                                            <span className={`text-sm ${childActive ? 'font-semibold' : 'font-medium'}`}>
                                              {child.label}
                                            </span>
                                          </Link>
                                        </li>
                                      );
                                    })}
                                  </ul>
                                </OathAnimatedPanel>
                              </li>
                            );
                          }

                          return (
                            <li key={item.path}>
                              <Link
                                to={item.path}
                                onClick={onClose}
                                title={item.tooltip}
                                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all ${
                                  active
                                    ? 'bg-primary text-white shadow-md shadow-primary/20'
                                    : 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800'
                                }`}
                              >
                                <Icon
                                  className={`h-5 w-5 flex-shrink-0 ${
                                    active ? 'text-white' : 'text-gray-400'
                                  }`}
                                />
                                <span className={active ? 'font-semibold' : 'font-medium'}>{item.label}</span>
                              </Link>
                            </li>
                          );
                        })}
                    </ul>
                  </nav>

                  {/* Logout */}
                  <div className="border-t border-gray-200 dark:border-gray-700 p-3 dark:bg-gray-900">
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-error bg-red-50 dark:bg-red-900/30 hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors"
                    >
                      <ArrowLeftStartOnRectangleIcon className="h-5 w-5 flex-shrink-0" />
                      <span className="font-semibold">Sign Out</span>
                    </button>
                  </div>
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default MobileDrawer;
